import type { OnboardingProgress, SectionStatus, SectionProgress } from '../types/onboarding';

const STORAGE_KEY = 'onboarding-progress';
const MEDICAL_HISTORY_KEY = 'medical-history-reviewed';
const MEDICATIONS_KEY = 'medications-reviewed';
const FAMILY_HISTORY_KEY = 'family-history-reviewed';
const MEDICAL_RECORDS_KEY = 'medical-records-reviewed';

export interface MedicalHistoryReviewed {
  noActiveConditions: boolean;
  noPriorEvents: boolean;
  noProcedures: boolean;
}

export interface MedicationsReviewed {
  noMedications: boolean;
  noSupplements: boolean;
  noAllergies: boolean;
}

export interface FamilyHistoryReviewed {
  noFamilyHistory: boolean;
}

export interface MedicalRecordsReviewed {
  noRecords: boolean;
}

// Generic factory for localStorage-backed reviewed flag stores
export function createReviewedStore<T extends { [K in keyof T]: boolean }>(key: string, defaults: T) {
  return {
    get(): T {
      const stored = localStorage.getItem(key);
      if (stored) {
        try {
          return JSON.parse(stored);
        } catch {
          return { ...defaults };
        }
      }
      return { ...defaults };
    },
    set(flags: Partial<T>): T {
      const current = this.get();
      const updated = { ...current, ...flags };
      localStorage.setItem(key, JSON.stringify(updated));
      return updated;
    },
    clear() {
      localStorage.removeItem(key);
    },
  };
}

// Create reviewed stores for each section
const medicalHistoryStore = createReviewedStore<MedicalHistoryReviewed>(MEDICAL_HISTORY_KEY, {
  noActiveConditions: false,
  noPriorEvents: false,
  noProcedures: false,
});

const medicationsStore = createReviewedStore<MedicationsReviewed>(MEDICATIONS_KEY, {
  noMedications: false,
  noSupplements: false,
  noAllergies: false,
});

const familyHistoryStore = createReviewedStore<FamilyHistoryReviewed>(FAMILY_HISTORY_KEY, {
  noFamilyHistory: false,
});

const medicalRecordsStore = createReviewedStore<MedicalRecordsReviewed>(MEDICAL_RECORDS_KEY, {
  noRecords: false,
});

// Migrate old format (completedSections array) to new format (sections record)
export function migrateProgress(stored: unknown): OnboardingProgress {
  if (!stored || typeof stored !== 'object') {
    return { sections: {} };
  }

  const obj = stored as Record<string, unknown>;

  // Already new format
  if (obj.sections && typeof obj.sections === 'object') {
    return { sections: obj.sections } as OnboardingProgress;
  }

  // Old format - migrate
  if (Array.isArray(obj.completedSections)) {
    const sections: Record<string, SectionProgress> = {};
    for (const id of obj.completedSections) {
      sections[id] = { status: 'complete' };
    }
    return { sections };
  }

  return { sections: {} };
}

export const onboardingService = {
  getProgress(): OnboardingProgress {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        return migrateProgress(JSON.parse(stored));
      } catch {
        return { sections: {} };
      }
    }
    return { sections: {} };
  },

  saveProgress(progress: OnboardingProgress): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
  },

  getSectionStatus(sectionId: string): SectionStatus {
    const progress = this.getProgress();
    return progress.sections[sectionId]?.status || 'unseen';
  },

  setSectionStatus(sectionId: string, status: SectionStatus): OnboardingProgress {
    const progress = this.getProgress();
    const now = new Date().toISOString();

    const existing = progress.sections[sectionId] || {};
    progress.sections[sectionId] = {
      ...existing,
      status,
      ...(status === 'in-progress' && !existing.lastViewed ? { lastViewed: now } : {}),
      ...(status === 'complete' ? { lastSaved: now } : {}),
    };

    this.saveProgress(progress);
    return progress;
  },

  markSectionViewed(sectionId: string): OnboardingProgress {
    const currentStatus = this.getSectionStatus(sectionId);
    // Only update if unseen - don't downgrade from complete
    if (currentStatus === 'unseen') {
      return this.setSectionStatus(sectionId, 'in-progress');
    }
    return this.getProgress();
  },

  markSectionComplete(sectionId: string): OnboardingProgress {
    return this.setSectionStatus(sectionId, 'complete');
  },

  markSectionIncomplete(sectionId: string): OnboardingProgress {
    return this.setSectionStatus(sectionId, 'in-progress');
  },

  isSectionComplete(sectionId: string): boolean {
    return this.getSectionStatus(sectionId) === 'complete';
  },

  clearProgress(): void {
    localStorage.removeItem(STORAGE_KEY);
  },

  // Medical History reviewed flags
  getMedicalHistoryReviewed: () => medicalHistoryStore.get(),
  setMedicalHistoryReviewed: (flags: Partial<MedicalHistoryReviewed>) => medicalHistoryStore.set(flags),
  clearMedicalHistoryReviewed: () => medicalHistoryStore.clear(),

  // Medications reviewed flags
  getMedicationsReviewed: () => medicationsStore.get(),
  setMedicationsReviewed: (flags: Partial<MedicationsReviewed>) => medicationsStore.set(flags),
  clearMedicationsReviewed: () => medicationsStore.clear(),

  // Family History reviewed flags
  getFamilyHistoryReviewed: () => familyHistoryStore.get(),
  setFamilyHistoryReviewed: (flags: Partial<FamilyHistoryReviewed>) => familyHistoryStore.set(flags),
  clearFamilyHistoryReviewed: () => familyHistoryStore.clear(),

  // Medical Records reviewed flags
  getMedicalRecordsReviewed: () => medicalRecordsStore.get(),
  setMedicalRecordsReviewed: (flags: Partial<MedicalRecordsReviewed>) => medicalRecordsStore.set(flags),
  clearMedicalRecordsReviewed: () => medicalRecordsStore.clear(),
};
